import React from "react";
import { Calendar, Clock, MapPin, Sparkles } from "lucide-react";
import type { EventData } from "@/services/sanity/types";
import { formatEventDateInfo } from "@/services/calendar/format-event";

interface EventCardProps {
    event: EventData;
    t: (key: string, options?: Record<string, unknown>) => string;
    onSelect?: (event: EventData) => void;
    badgeText?: string;
}

export const EventCard: React.FC<EventCardProps> = ({ event, t, onSelect, badgeText }) => {
    const handleCardClick = () => {
        if (onSelect) {
            onSelect(event);
        }
    };

    const dateInfo = formatEventDateInfo(event, t);
    const effectiveBadge =
        event.customTag ||
        (event.isHighlighted ? t("events.featuredEventBadge") || "Featured Event" : badgeText);

    return (
        <div
            onClick={handleCardClick}
            className={`group bg-white dark:bg-warm-dark-surface rounded-2xl overflow-hidden transition-all flex flex-col justify-between text-left ${
                event.isHighlighted
                    ? "ring-2 ring-primary/40 border border-primary/50 shadow-md hover:shadow-lg"
                    : "border border-warm-border dark:border-warm-dark-border shadow-sm hover:shadow-md"
            } ${onSelect ? "cursor-pointer" : ""}`}
        >
            <div>
                {/* Event Poster Thumbnail Banner */}
                {event.image ? (
                    <div className="relative w-full aspect-[16/9] bg-stone-900 overflow-hidden">
                        <img
                            src={event.image}
                            alt={`${event.title} flyer`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
                        {effectiveBadge && (
                            <div className="absolute top-3 left-3 flex flex-wrap gap-1.5">
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary text-white shadow-sm">
                                    <Sparkles size={12} className={event.isHighlighted ? "text-amber-300" : ""} />
                                    <span>{effectiveBadge}</span>
                                </span>
                            </div>
                        )}
                        <div className="absolute bottom-2 right-3 text-xs text-white/90 font-medium">
                            {t("events.tapToViewFlyer") || "Tap to view flyer"} &rarr;
                        </div>
                    </div>
                ) : null}

                <div className="p-6">
                    {/* Header if no poster banner */}
                    {!event.image && (
                        <div className="flex items-center justify-between mb-4">
                            <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                                <Calendar size={20} />
                            </div>
                            {effectiveBadge && (
                                <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-primary/10 text-primary">
                                    {effectiveBadge}
                                </span>
                            )}
                        </div>
                    )}

                    <h3 className="font-serif text-xl font-bold text-secondary dark:text-white mb-3 group-hover:text-primary transition-colors line-clamp-2">
                        {event.title}
                    </h3>

                    <div className="space-y-2 text-stone-600 dark:text-stone-300 text-sm mb-6">
                        <div className="flex items-start gap-2">
                            <Clock size={16} className="text-primary mt-0.5 shrink-0" />
                            <div>
                                <div className="font-medium text-secondary dark:text-white">
                                    {dateInfo.formattedDate}
                                </div>
                                {dateInfo.formattedTime && (
                                    <div className="text-xs text-stone-500 dark:text-stone-400">
                                        {dateInfo.formattedTime}
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className="flex items-start gap-2">
                            <MapPin size={16} className="text-primary mt-0.5 shrink-0" />
                            <span className="line-clamp-2">
                                {event.location}
                                {event.address && event.address !== event.location && (
                                    <>
                                        <br />
                                        {event.address}
                                    </>
                                )}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Actions footer */}
            <div className="p-6 pt-0 flex gap-2">
                {onSelect && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onSelect(event);
                        }}
                        className="flex-1 text-center py-2.5 px-3 rounded-lg bg-stone-100 dark:bg-warm-dark-bg hover:bg-stone-200 dark:hover:bg-stone-800 text-stone-700 dark:text-stone-200 text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        {event.image ? t("events.viewFlyer") || "View Flyer" : t("events.details") || "Details"}
                    </button>
                )}

                {event.googleMapsLink && (
                    <a
                        href={event.googleMapsLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="flex-1 text-center py-2.5 px-3 rounded-lg border border-warm-border dark:border-warm-dark-border text-stone-700 dark:text-stone-200 hover:bg-warm-stone/50 dark:hover:bg-warm-dark-bg transition-colors text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary"
                    >
                        {t("events.getDirections")}
                    </a>
                )}
            </div>
        </div>
    );
};
